<template>
    <b-col class="report-widget" :class="timestampStatusClass">

        <b-card no-body
                v-if="!widget.rtcMeters"
                @click.stop="showModal()"
                :class="{'no-modal' : !enableModal}">
            <!-- meters/other (e.g. Framingham) -->
            <b-card-body class="report-widget__body__meter">
                <div class="report-widget__title">
                    {{ $i18n.gt(widget.headingKey) }}
                </div>

                <div v-for="(m,i) in widget.meters" :key="`meter_${i}`">
                    <report-meter :score-name="m.scoreName"
                                  :label="getMeterLabel(m)"
                                  :custom-gauge-colors="widget.customGaugeColors">
                    </report-meter>
                </div>

                <!-- slot for Framingham, ACC_AHA (or other custom content) -->
                <slot></slot>

                <div class="report-widget__timestamp">
                    <timestamp :date="timestamp.value"></timestamp>
                </div>
            </b-card-body>
        </b-card>
        <b-row v-else style="cursor: default">
            <div class="report-widget__title col">
                {{ $i18n.gt(widget.headingKey) }}
            </div>
            <div class="col" v-for="(m,i) in widget.rtcMeters" :key="`meter_${i}`">
                <report-meter-rtc :score-name="m.scoreName"
                                  :scaleTo5="rtcScale(m)">
                </report-meter-rtc>
            </div>
        </b-row>


        <!-- details modal -->

        <report-widget-details-modal v-if="widget.meters && enableModal" :widget-key="widgetKey">
            <!-- slot for Framingham, ACC_AHA (or other custom content) -->
            <slot></slot>
        </report-widget-details-modal>

    </b-col>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import ReportMixin from '@/providers/ReportMixin'
import Utils from '@/providers/Utils'

/**
 * Displays relevant information for an individual score (or score group) - including
 * linear gauges, selected plan of care resources, and advice.
 * Clicking the widget opens a modal with additional information.
 */
export default Vue.extend({
    name: 'ReportWidget',
    mixins: [ReportMixin],
    props: {
        widgetKey: String,
        timeInterval: Number, // optional
        enableModal: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        widget() {
            return this.getWidgets()[this.widgetKey] // see ReportMixin
        },
        timestamp() {
            if (this.$store.state.patientParticipant.latestScoreDefs) {
                let scores = []
                if (this.widget.meters) {
                    // if this is a compound widget, get the latest timestamp for any component meter
                    scores = _.map(this.widget.meters, (meter) => {
                        const scoreDef = this.$store.state.patientParticipant.latestScoreDefs[meter.scoreName]
                        if (scoreDef && scoreDef.score.updated) {
                            return scoreDef.score
                        } else {
                            return {updated: null}
                        }
                    })
                } else {
                    const scoreDef = this.$store.state.patientParticipant.latestScoreDefs[this.widgetKey]
                    scores = scoreDef ? [scoreDef.score] : []
                }
                const lastScore = _.maxBy(scores, (score) => score.updated)
                if (lastScore && lastScore.updated) {
                    return {
                        value: lastScore.updated,
                        recent: Utils.dateIsLessThanMonthsAgo(lastScore.updated, this.timeInterval)
                    }
                }
            }
            return {value: 'No Data', recent: false}
        },
        timestampStatusClass() {
            if (!this.timeInterval) {
                return 'report-widget__status__recent'
            } else if (this.timestamp.value == 'No Data') {
                return 'report-widget__status__missing'
            } else if (!this.timestamp.recent) {
                return 'report-widget__status__old'
            } else {
                return 'report-widget__status__recent'
            }
        },
        modalId() {
            return `modal_${this.widgetKey}`
        },
    },
    methods: {
        showModal() {
            if (this.enableModal) this.$bvModal.show(this.modalId)
        },
        rtcScale(meter) {
            if (meter.scoreName) {
                return meter.scoreName.slice(-3) === 'RTC'
            } else {
                return false
            }
        }
    }
})
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

.report-widget {
    cursor: pointer;

    .card {
        background: #fff;
        transition: all .3s;
        margin-bottom: 0;
        @media(min-width: 540px) {
            height: 100%;
        }

        &:hover {
            box-shadow: 0 0 10px rgba(0, 0, 0, .15);

            .report-widget__timestamp {
                color: var(blue);
            }
        }
    }

    @media(max-width: 719px) {
        margin-bottom: 1rem;
    }

    .no-modal {
        box-shadow: none;
        border: none;
        margin-bottom: 10px;
        cursor: default;
        transition: none;
        &:hover {
            box-shadow: none;
        }
    }
}

.report-widget__status__recent {
}

.report-widget__status__old {
    opacity: 0.3;
}

.report-widget__status__missing {
    opacity: 0.3;
}

.report-widget__body__meter {
    padding-top: 1rem !important;
    padding-bottom: 2rem !important;
}

.report-widget__title {
    text-transform: uppercase;
    color: #666;
    font-size: .9em;
    text-align: center;
}

.report-widget__option-list {
    margin: 10px;
}

.report-widget__timestamp {
    position: absolute;
    right: 10px;
    bottom: 5px;
    color: #888;
    font-size: .85em;
    font-weight: 300;
}

</style>
